import { apiCommand, apiGet, apiSend, unwrapItems } from '@/shared/api/request';
import type { OrderStatusValue } from '@/shared/model/order-status';

/**
 * API раздела «Заказы» (AUI-ORD).
 *
 * Типы описаны руками по контракту `docs/contracts/order-lifecycle.md` (актуален на 3 сентября
 * 2026 года) и § 5 контракта оплаты: снимок OpenAPI в репозитории пуст — спека админки закрыта
 * аутентификацией, и `pnpm api:sync` без токена сотрудника её не забирает. После синхронизации
 * спеки файл должен перейти на сгенерированные типы `@/shared/api/schema`.
 *
 * Раздел собран из двух модулей: заказ и его жизненный цикл — из `order`, платежи — из `payment`.
 * Общего пути у них нет: платёж живёт своей жизнью и переживает заказ, а карточка показывает
 * только последний.
 */

/* ================================ Статусы ================================ */

/**
 * Статус заказа в контракте. Семь значений объединённой модели ([FR-ORD-01]); возврат статусом
 * не является — это операция над завершённым заказом.
 */
export type OrderStatusCode =
  'NEW' | 'PAID' | 'SHIPPED' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD' | 'UNDEFINED';

export const orderStatusCodes: OrderStatusCode[] = [
  'NEW',
  'PAID',
  'SHIPPED',
  'COMPLETED',
  'ON_HOLD',
  'CANCELLED',
  'UNDEFINED',
];

/**
 * Код контракта — в значение словаря оформления (`@/shared/model/order-status`).
 *
 * Словарь админки старше контракта и назван по-своему («done», «held»): он же красит статусы
 * на дашборде. Переименовывать его под бэкенд значило бы править дашборд ради заказов,
 * поэтому перевод живёт здесь, на границе с API.
 */
const STATUS_VIEW: Record<OrderStatusCode, OrderStatusValue> = {
  NEW: 'new',
  PAID: 'paid',
  SHIPPED: 'shipped',
  COMPLETED: 'done',
  CANCELLED: 'cancelled',
  ON_HOLD: 'held',
  UNDEFINED: 'undefined',
};

export function statusView(status: OrderStatusCode): OrderStatusValue {
  return STATUS_VIEW[status];
}

/** Способ оплаты из снимка заказа. Перечень закрыт контрактом оплаты. */
export type PaymentMethod = 'CARD' | 'ERIP' | 'CASH_ON_DELIVERY';

export const paymentMethodLabels: Record<PaymentMethod, string> = {
  CARD: 'Карта онлайн',
  ERIP: 'ЕРИП',
  CASH_ON_DELIVERY: 'Наложенный платёж',
};

/** Состояние последнего платежа заказа. */
export type PaymentStatus = 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'EXPIRED' | 'CANCELLED';

export const paymentStatusLabels: Record<PaymentStatus, string> = {
  PENDING: 'Ожидает оплаты',
  SUCCEEDED: 'Оплачен',
  FAILED: 'Отклонён',
  EXPIRED: 'Просрочен',
  CANCELLED: 'Отменён',
};

/* ================================= Список ================================= */

/**
 * Наклейки заказа ([AUI-ORD-01c], [B-ORD-21]).
 *
 * Считает их бэкенд: `paid` — есть успешный платёж (у наложенного — после отметки «оплачен»),
 * `ru` — страна адреса доставки `RU`. Выводить их из полей строки админка не имеет права:
 * «оплачен» — это факт из модуля платежей, а не статус заказа.
 */
export interface OrderBadges {
  paid: boolean;
  ru: boolean;
}

/**
 * Получатель из снимка заказа.
 *
 * `customerId` — ссылка в карточку покупателя ([AUI-ORD-03]); у заказа гостя его нет.
 * Имя может отличаться от анкеты: заказ оформляли на получателя, а не на владельца аккаунта.
 */
export interface OrderCustomer {
  customerId: string | null;
  lastName: string | null;
  firstName: string | null;
  phone: string;
}

export interface OrderListItem {
  id: string;
  /** Человеческий номер заказа — «A-000124». По нему заказ ищут и о нём говорят. */
  number: string;
  createdAt: string;
  customer: OrderCustomer;
  total: string;
  status: OrderStatusCode;
  /** Прежний статус заказа на паузе: `/resume` вернёт ровно в него. */
  heldFromStatus: OrderStatusCode | null;
  paymentMethod: PaymentMethod;
  deliveryMethodCode: string;
  deliveryMethodName: string;
  trackNumber: string | null;
  badges: OrderBadges;
  updatedAt: string;
}

export interface OrderPage {
  items: OrderListItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

/**
 * Порядок списка.
 *
 * `ATTENTION_FIRST` поднимает `NEW` и `UNDEFINED` — это рабочая очередь менеджера, ради неё
 * раздел и открывают. `NEWEST` — просто лента, ею смотрят историю.
 */
export type OrderSort = 'NEWEST' | 'ATTENTION_FIRST';

/**
 * Период отбора по дате оформления.
 *
 * В адресе живёт название периода, а не пара отметок времени: ссылка «заказы за сегодня»,
 * открытая завтра, должна показать завтрашний день, а не вчерашние сутки. Границы считаются
 * в момент запроса (`periodStart`).
 */
export type OrderPeriod = 'all' | 'today' | 'week' | 'month';

export interface OrderFilters {
  statuses: OrderStatusCode[];
  period: OrderPeriod;
  paymentMethod: PaymentMethod | null;
  deliveryMethod: string | null;
  search: string;
  sort: OrderSort;
}

export interface OrderQuery extends OrderFilters {
  cursor?: string;
  /** Размер страницы; без него — умолчание бэкенда. Дашборду нужно десять строк, а не ленту. */
  limit?: number;
}

/**
 * Начало периода в ISO-8601 с зоной.
 *
 * Сутки считаются по календарю браузера, а не вычитанием 24 часов: «сегодня» — утверждение
 * про календарь, и заказ, оформленный ночью, из него выпадать не должен. «7 дней» и «30 дней» —
 * это тоже целые сутки назад, а не «ровно 168 часов»: иначе список менялся бы каждую минуту.
 */
export function periodStart(period: OrderPeriod, now: Date = new Date()): string | null {
  if (period === 'all') {
    return null;
  }
  const days = period === 'today' ? 0 : period === 'week' ? 6 : 29;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
  return start.toISOString();
}

/** Общий корень ключей списка: по нему строки правятся на месте после действия. */
export const ORDERS_LIST_KEY = ['orders', 'list'] as const;

export function ordersListKey(filters: OrderFilters) {
  return [
    ...ORDERS_LIST_KEY,
    filters.statuses.join(',') || null,
    filters.period,
    filters.paymentMethod,
    filters.deliveryMethod,
    filters.search,
    filters.sort,
  ];
}

export function orderCardKey(id: string) {
  return ['orders', 'card', id];
}

export function orderDocumentsKey(id: string) {
  return ['orders', 'documents', id];
}

/**
 * Страница списка заказов.
 *
 * Пустые параметры не уходят вовсе: `status=` и `search=` — это не «без отбора», а строка,
 * которую бэкенд обязан как-то истолковать, и толкование это ничем не обещано. `sort` уходит
 * всегда: он часть курсора, и договариваться о нём молчанием нельзя — курсор, выданный
 * одним порядком, при другом отвечает `400 bad-request`.
 */
export async function fetchOrders(query: OrderQuery): Promise<OrderPage> {
  const params = new URLSearchParams();
  for (const status of query.statuses) {
    params.append('status', status);
  }
  const from = periodStart(query.period);
  if (from) {
    params.set('createdFrom', from);
  }
  if (query.paymentMethod) {
    params.set('paymentMethod', query.paymentMethod);
  }
  if (query.deliveryMethod) {
    params.set('deliveryMethod', query.deliveryMethod);
  }
  if (query.search) {
    params.set('search', query.search);
  }
  params.set('sort', query.sort);
  if (query.cursor) {
    params.set('cursor', query.cursor);
  }
  if (query.limit) {
    params.set('limit', String(query.limit));
  }
  const body = await apiGet<Partial<OrderPage>>(`/v1/admin/orders?${params.toString()}`);
  return {
    items: unwrapItems<OrderListItem>(body),
    nextCursor: body.nextCursor ?? null,
    hasMore: body.hasMore ?? false,
  };
}

/**
 * Заказы, требующие внимания: `NEW` и `UNDEFINED` ([B-ORD-26]). Пауза в счёт не входит — как
 * и в группе внимания списка. Отдельный запрос, а не поле страницы: общего числа курсорная
 * страница не содержит намеренно.
 */
export interface OrderCounters {
  attention: number;
}

/** Не под корнем списков: правка строк на месте не должна пытаться разобрать счётчик как страницу. */
export const ORDER_COUNTERS_KEY = ['orders', 'counters'] as const;

export async function fetchOrderCounters(): Promise<OrderCounters> {
  return apiGet<OrderCounters>('/v1/admin/orders/counters');
}

/* ================================ Карточка ================================ */

/**
 * Себестоимость позиции по статьям на момент оформления (Р13).
 *
 * Видна только администратору; менеджер заказов получает `null` — поле есть, права нет.
 */
export interface OrderLineCost {
  purchase: string | null;
  packaging: string | null;
  assembly: string | null;
  other: string | null;
  total: string | null;
}

export interface OrderLine {
  position: number;
  unitId: string;
  productId: string;
  variantId: string | null;
  productName: string;
  variantName: string | null;
  sku: string;
  unitPrice: string;
  /** Цена до скидки, если позиция покупалась по акции. */
  unitOldPrice: string | null;
  quantity: number;
  bonusPointsPerUnit: number;
  lineTotal: string;
  cost: OrderLineCost | null;
  profit: string | null;
}

/**
 * Итоги заказа из снимка.
 *
 * Считать их заново по строкам админка не должна: в снимке цена и скидка такие, какими были
 * в момент оформления, а пересчёт по сегодняшнему каталогу дал бы другую сумму.
 */
export interface OrderTotals {
  goods: string;
  discount: string;
  delivery: string;
  bonusPoints: number;
  bonusMoney: string;
  total: string;
}

export interface OrderDelivery {
  methodCode: string;
  methodName: string;
  price: string;
  parcels: number;
  country: string;
  /** У самовывоза адреса нет: заказ забирают из магазина. */
  address: string | null;
}

export interface OrderPayment {
  method: PaymentMethod;
  /** Срок ожидания оплаты; у наложенного платежа его нет и он не истекает. */
  deadlineAt: string | null;
  /** Последний платёж заказа. `null` — платежей ещё не было. */
  paymentId: string | null;
  status: PaymentStatus | null;
  failureCode: string | null;
}

export interface OrderRecipient {
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  phone: string;
  email: string | null;
}

/** Отметки согласий покупателя на момент оформления: оферта и обработка персональных данных. */
export interface OrderConsents {
  offerAcceptedAt: string | null;
  personalDataAcceptedAt: string | null;
}

/** Кто сделал запись журнала. `STAFF` — сотрудник админки, `SYSTEM` — автоотмена и колбэки шлюза. */
export type JournalActor = 'CUSTOMER' | 'STAFF' | 'SYSTEM';

/**
 * Запись журнала заказа ([B-ORD-11]).
 *
 * `fromStatus = toStatus` — отметка без перехода: `delivery-edited`, `manual-refund-required`
 * и другие обстоятельства, которые заказ обязан помнить, но статусом не являются.
 */
export interface OrderJournalEntry {
  id: string;
  fromStatus: OrderStatusCode | null;
  toStatus: OrderStatusCode;
  actor: JournalActor;
  actorId: string | null;
  cause: string | null;
  comment: string | null;
  createdAt: string;
}

export interface Order {
  id: string;
  number: string;
  customerId: string | null;
  status: OrderStatusCode;
  heldFromStatus: OrderStatusCode | null;
  createdAt: string;
  updatedAt: string;
  trackNumber: string | null;
  paymentDeadlineAt: string | null;
  lines: OrderLine[];
  totals: OrderTotals;
  cost: string | null;
  profit: string | null;
  delivery: OrderDelivery;
  payment: OrderPayment;
  recipient: OrderRecipient;
  consents: OrderConsents;
  comment: string | null;
  badges: OrderBadges;
  /** Куда заказ может перейти сейчас: кнопки действий рисуются по нему, а не по своей матрице. */
  allowed: OrderStatusCode[];
  journal: OrderJournalEntry[];
}

export async function fetchOrder(id: string): Promise<Order> {
  return withLists(await apiGet<Order>(`/v1/admin/orders/${encodeURIComponent(id)}`));
}

/**
 * Списки карточки, которых может не оказаться в ответе.
 *
 * Пропущенный массив уронил бы карточку целиком на первом же `map`, тогда как пустой список
 * рисуется своей заглушкой и остальную карточку не трогает.
 */
function withLists(order: Order): Order {
  return { ...order, lines: order.lines ?? [], journal: order.journal ?? [], allowed: order.allowed ?? [] };
}

/* ================================ Действия ================================ */

/**
 * Ответ действия — короткий снимок заказа, а не карточка целиком.
 *
 * Ровно то, что меняется переходом: статус, трек, набор следующих шагов. Карточка после
 * действия перечитывается отдельно — журнал и побочные отметки (`manual-refund-required`)
 * рождаются в той же транзакции, и собирать их из ответа по кусочкам было бы гаданием.
 */
export interface OrderActionResult {
  id: string;
  number: string;
  status: OrderStatusCode;
  heldFromStatus: OrderStatusCode | null;
  trackNumber: string | null;
  allowed: OrderStatusCode[];
  updatedAt: string;
}

function action(id: string, path: string, body?: unknown): Promise<OrderActionResult> {
  return apiSend<OrderActionResult>('POST', `/v1/admin/orders/${encodeURIComponent(id)}/${path}`, body ?? {});
}

/** Отмена. Причина обязательна и остаётся в журнале — это единственное объяснение отказа. */
export function cancelOrder(id: string, reason: string): Promise<OrderActionResult> {
  return action(id, 'cancel', { reason });
}

/** Передача в доставку. Трек-номер и комментарий необязательны: посылку могли отдать без трека. */
export function shipOrder(
  id: string,
  fields: { trackNumber?: string; comment?: string },
): Promise<OrderActionResult> {
  return action(id, 'ship', fields);
}

/** Завершение. У наложенного платежа требует отметки «оплачен», иначе 409 `payment-not-confirmed`. */
export function completeOrder(id: string, comment?: string): Promise<OrderActionResult> {
  return action(id, 'complete', comment ? { comment } : {});
}

/** Пауза. Комментарий обязателен: пауза без причины — заказ, о котором забыли. */
export function holdOrder(id: string, comment: string): Promise<OrderActionResult> {
  return action(id, 'hold', { comment });
}

/** Снятие паузы: заказ возвращается ровно в тот статус, из которого его отложили. */
export function resumeOrder(id: string, comment?: string): Promise<OrderActionResult> {
  return action(id, 'resume', comment ? { comment } : {});
}

/** Перевод в «Неопределён»: с заказом что-то не так и разбираться будет человек. */
export function undefineOrder(id: string, comment: string): Promise<OrderActionResult> {
  return action(id, 'undefined', { comment });
}

/**
 * Разбор «Неопределён»: заказ выводится в один из обычных статусов.
 *
 * Трек уходит только при выходе в `SHIPPED` — в остальных случаях ему неоткуда взяться,
 * и лишнее поле бэкенд отклонит.
 */
export function resolveOrder(
  id: string,
  fields: { status: OrderStatusCode; comment: string; trackNumber?: string },
): Promise<OrderActionResult> {
  return action(id, 'resolve', fields);
}

/* ========================== Правка данных доставки ========================== */

/**
 * Правка адреса, телефона получателя и комментария ([B-ORD-24]).
 *
 * Способ и стоимость доставки не правятся (Р10): они входят в итог, за который покупатель
 * заплатил. Поле `null` бэкенд трактует как «не трогать», поэтому пустая строка сюда
 * не отправляется — она означала бы «стереть», чего контракт не предусматривает.
 */
export interface DeliveryPatch {
  address?: string;
  phone?: string;
  comment?: string;
}

export async function editOrderDelivery(id: string, patch: DeliveryPatch): Promise<Order> {
  return withLists(
    await apiSend<Order>('PATCH', `/v1/admin/orders/${encodeURIComponent(id)}/delivery`, patch),
  );
}

/* ================================= Печать ================================= */

export interface DocumentStore {
  name: string;
  legalName: string | null;
  taxNumber: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
}

export interface DocumentLine {
  title: string;
  sku: string;
  quantity: number;
  unitPrice: string;
  lineTotal: string;
}

export interface DocumentTotal {
  label: string;
  amount: string;
}

/** Подтверждение заказа для покупателя: строки собраны бэкендом, админка их только верстает. */
export interface OrderConfirmation {
  store: DocumentStore;
  number: string;
  /** Уже отформатированная дата в поясе магазина — «3 сентября 2026, 13:15». */
  placedAt: string;
  recipient: string;
  phone: string;
  email: string | null;
  delivery: string;
  payment: string;
  lines: DocumentLine[];
  totals: DocumentTotal[];
  comment: string | null;
}

/** Адресный лист на отправление. У самовывоза его нет — забирать посылку будут из магазина. */
export interface AddressLabel {
  sender: DocumentStore;
  recipient: string;
  phone: string;
  address: string;
  country: string;
  number: string;
  deliveryMethod: string;
  parcels: number;
}

export interface OrderDocuments {
  confirmation: OrderConfirmation;
  addressLabel: AddressLabel | null;
}

export async function fetchOrderDocuments(id: string): Promise<OrderDocuments> {
  const body = await apiGet<OrderDocuments>(`/v1/admin/orders/${encodeURIComponent(id)}/documents`);
  return {
    ...body,
    confirmation: {
      ...body.confirmation,
      lines: body.confirmation?.lines ?? [],
      totals: body.confirmation?.totals ?? [],
    },
  };
}

/* ================================= Платежи ================================= */

/**
 * Отметка наложенного платежа оплаченным (§ 5 контракта оплаты).
 *
 * Живёт в модуле платежей, а не заказа, и адресуется идентификатором платежа: деньги получены
 * за платёж, а не за заказ. Без этой отметки завершение заказа с наложенным платежом отвечает
 * 409 `payment-not-confirmed` — заказ считался бы закрытым, а деньги неполученными.
 */
export function markCashPaid(paymentId: string): Promise<void> {
  return apiCommand('POST', `/v1/admin/payments/${encodeURIComponent(paymentId)}/cash-paid`, {});
}

/* ================================= Подписи ================================= */

/**
 * Подпись покупателя в строке списка — «Иванов Иван» или, если имени в снимке нет, номер.
 *
 * Инициалы здесь не сокращаются, в отличие от списка покупателей: в заказе фамилия и имя —
 * это получатель, которого называют на почте целиком.
 */
export function orderCustomerName(customer: OrderCustomer): string {
  const name = [customer.lastName, customer.firstName].filter(Boolean).join(' ');
  return name || customer.phone;
}

/** Полное ФИО получателя для карточки; без анкеты — номер телефона, он есть всегда. */
export function recipientName(recipient: OrderRecipient): string {
  const name = [recipient.lastName, recipient.firstName, recipient.middleName].filter(Boolean).join(' ');
  return name || recipient.phone;
}

/**
 * Причины записей журнала, у которых есть человеческое название.
 *
 * Незнакомый код показывается как есть: журнал пополняется на бэкенде, и молча прятать
 * неизвестную причину значило бы скрывать от менеджера ровно то, ради чего он открыл журнал.
 */
export const JOURNAL_CAUSES: Record<string, string> = {
  'payment-succeeded': 'Оплата подтверждена',
  'payment-expired': 'Срок оплаты истёк — автоотмена',
  'cash-payment-received': 'Наложенный платёж отмечен оплаченным',
  'manager-cancelled': 'Отменён менеджером',
  'delivery-edited': 'Правка данных доставки',
  'manual-refund-required': 'Требуется ручной возврат денег',
  'manual-bonus-reversal-required': 'Требуется ручное сторно бонусов',
  'reservation-expired-on-commit': 'Резерв остатка истёк до подтверждения',
  'bonus-hold-expired-on-commit': 'Удержание бонусов истекло до подтверждения',
};
